import { DICTIONARY } from './data/dictionary';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from './languages';

/** Definition + optional origin note for the insight feature (spec §3.7). */
export interface WordInsight {
  def: string;
  /**
   * Etymology note, when the source has one. Always absent for Dutch:
   * Open Dutch WordNet carries definitions only.
   */
  origin?: string;
}

/**
 * Entries registered at runtime by `insightsLazy.loadInsightsFor`, one map
 * per language.
 *
 * The English 5-letter dictionary is bundled; every other length — and
 * every Dutch length — arrives as its own chunk (spec §10.5). Merging them
 * into module-level maps here is what lets `insightFor` stay *synchronous*,
 * so every existing caller — the result sheet, the insight sheet,
 * GamePlay's reveal gate — keeps working untouched. The maps are
 * append-only: registering the same chunk twice is a no-op, and nothing is
 * ever removed.
 *
 * Splitting by language is not tidiness. `water`, `rat`, `angel` and `wet`
 * are ordinary words in both languages with entirely different meanings; a
 * single shared map would answer a Dutch game with whichever chunk had
 * loaded last.
 */
const registered: Readonly<Record<Language, Record<string, WordInsight>>> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((language) => [language, Object.create(null) as Record<string, WordInsight>]),
) as Record<Language, Record<string, WordInsight>>;

export function registerInsights(
  entries: Readonly<Record<string, WordInsight>>,
  language: Language = DEFAULT_LANGUAGE,
): void {
  Object.assign(registered[language], entries);
}

/** Case-insensitive lookup; null when we have no data for the word. */
export function insightFor(
  word: string,
  language: Language = DEFAULT_LANGUAGE,
): WordInsight | null {
  const w = word.toLowerCase();
  if (language !== DEFAULT_LANGUAGE) return registered[language][w] ?? null;
  return DICTIONARY[w] ?? registered[DEFAULT_LANGUAGE][w] ?? null;
}
